import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { FeeService } from '../service/fee.service';
import { GroupDetailResponse, MemberProfileResponse, SubgroupResponse } from '../interface/fee.interface';
import { SnackBarUtil } from '../qr-copy-message/snack-bar-util.service';
import { MockupDataModel } from '../qr-copy-message/mockup-data.model';
import {
  FeeEditPersonalInfoDialogComponent,
  EditPersonalInfoDialogData
} from '../fee-edit-personal-info-dialog/fee-edit-personal-info-dialog.component';
import { formatApiDateString } from '../utils/date.utils';
import {
  SubgroupFormDialogComponent,
  SubgroupFormDialogData
} from '../fee-subgroup-form-dialog/fee-subgroup-form-dialog.component';
import {
  SubgroupDeleteDialogComponent,
  SubgroupDeleteDialogData
} from '../fee-subgroup-delete-dialog/fee-subgroup-delete-dialog.component';
import {
  GroupNameEditDialogComponent,
  GroupNameEditDialogData
} from '../fee-group-name-edit-dialog/fee-group-name-edit-dialog.component';
import {
  GroupDeleteDialogComponent,
  GroupDeleteDialogData
} from '../fee-group-delete-dialog/fee-group-delete-dialog.component';
import { FeeStateService } from '../service/fee-state.service';
import { FeeMemberResponse } from '../interface/fee.interface';
import { TranslateService } from '@ngx-translate/core';

interface TabItem {
  sg: SubgroupResponse;
  idx: number;
}

interface GroupPageMemberRow {
  id: number;
  subgroupId: number;
  name: string;
  frequency: string;
  discount: string;
  due: number;
  total: number;
  active: boolean;
  paymentStatus: string;
}

@Component({
  selector: 'app-fee-group-page',
  templateUrl: './fee-group-page.component.html',
  styleUrls: ['./fee-group-page.component.scss']
})
export class FeeGroupPageComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  industryId!: number;
  groupId!: number;
  group: GroupDetailResponse | null = null;
  activeSubgroupIndex = -1;
  tabScrollOffset = 0;
  visibleTabCount = 4;
  memberRows: GroupPageMemberRow[] = [];
  openedMemberMenuKey: string | null = null;
  memberMenuPosition: { top: number; left: number } | null = null;
  sendingNotificationMemberKey: string | null = null;
  private readonly memberProfileCache = new Map<number, MemberProfileResponse>();
  isMembersLoading = false;
  searchTerm = '';
  readonly loadMorePageSize = 10;
  membersVisibleCount = this.loadMorePageSize;
  isLoading = false;
  readonly currencySymbol = '֏';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly feeService: FeeService,
    private readonly feeStateService: FeeStateService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly snackBar: SnackBarUtil
  ) {}

  ngOnInit(): void {
    this.industryId = Number(this.route.snapshot.params['id']);
    this.groupId    = Number(this.route.snapshot.params['groupId']);
    const initialSubgroupId = this.route.snapshot.queryParamMap.get('subgroupId');
    this.updateVisibleTabCount();
    this.feeService.getUserIndustryById(this.industryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userIndustry => {
        this.feeStateService.setActiveUserIndustry(userIndustry);
      });
    this.loadGroup(initialSubgroupId ? Number(initialSubgroupId) : null);
  }

  ngOnDestroy(): void {
    this.feeStateService.setActiveUserIndustry(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openedMemberMenuKey = null;
  }

  memberRowKey(member: GroupPageMemberRow): string {
    return `${member.id}-${member.subgroupId}`;
  }

  get activeMenuMember(): GroupPageMemberRow | null {
    if (!this.openedMemberMenuKey) {
      return null;
    }
    return this.displayedMemberRows.find(m => this.memberRowKey(m) === this.openedMemberMenuKey)
      ?? this.memberRows.find(m => this.memberRowKey(m) === this.openedMemberMenuKey)
      ?? null;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateVisibleTabCount();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.openedMemberMenuKey) {
      this.closeMemberMenu();
    }
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.isLoading) {
      this.loadMore();
    }
  }

  private updateVisibleTabCount(): void {
    const w = window.innerWidth;
    if (w <= 800)      this.visibleTabCount = 100;
    else if (w < 992)  this.visibleTabCount = 4;
    else               this.visibleTabCount = 6;
  }

  private loadGroup(initialSubgroupId: number | null = null): void {
    this.feeService.getGroupDetail(this.groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(g => {
        this.group = g;
        if (initialSubgroupId !== null) {
          const idx = g.subgroups.findIndex(sg => sg.id === initialSubgroupId);
          if (idx >= 0) {
            this.activeSubgroupIndex = idx;
            this.tabScrollOffset = Math.max(0, idx - Math.floor(this.visibleTabCount / 2));
          }
        }
        this.loadMembersForCurrentSelection();
      });
  }

  get visibleSubgroupTabs(): TabItem[] {
    if (!this.group) return [];
    return this.group.subgroups
      .map((sg, idx) => ({ sg, idx }))
      .slice(this.tabScrollOffset, this.tabScrollOffset + this.visibleTabCount);
  }

  get canScrollLeft(): boolean {
    return this.tabScrollOffset > 0;
  }

  get canScrollRight(): boolean {
    return this.tabScrollOffset + this.visibleTabCount < (this.group?.subgroups.length ?? 0);
  }

  scrollLeft(): void  { if (this.canScrollLeft)  this.tabScrollOffset--; }
  scrollRight(): void { if (this.canScrollRight) this.tabScrollOffset++; }

  selectAllMembers(): void {
    this.activeSubgroupIndex = -1;
    this.searchTerm = '';
    this.resetMembersVisibleCount();
    this.loadMembersForCurrentSelection();
  }

  selectSubgroup(idx: number): void {
    this.activeSubgroupIndex = idx;
    this.searchTerm = '';
    this.resetMembersVisibleCount();
    this.loadMembersForCurrentSelection();
  }

  get activeSubgroup(): SubgroupResponse | null {
    if (this.activeSubgroupIndex < 0 || !this.group) return null;
    return this.group.subgroups[this.activeSubgroupIndex] ?? null;
  }

  get totalMembersCount(): number {
    if (!this.group) return 0;
    return this.group.subgroups.reduce((sum, sg) => sum + (sg.memberCount ?? 0), 0);
  }

  get filteredMemberRows(): GroupPageMemberRow[] {
    const keyword = this.searchTerm.trim().toLowerCase();
    if (!keyword) return this.memberRows;

    return this.memberRows.filter(member =>
      member.name.toLowerCase().includes(keyword)
      || member.frequency.toLowerCase().includes(keyword)
      || member.paymentStatus.toLowerCase().includes(keyword)
    );
  }

  get displayedMemberRows(): GroupPageMemberRow[] {
    return this.filteredMemberRows.slice(0, this.membersVisibleCount);
  }

  get hasMoreMemberRows(): boolean {
    return this.membersVisibleCount < this.filteredMemberRows.length;
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.resetMembersVisibleCount();
  }

  loadMore(): void {
    if (this.hasMoreMemberRows) {
      this.membersVisibleCount += this.loadMorePageSize;
    }
  }

  formatDate(d: string): string {
    return formatApiDateString(d, '.');
  }

  formatDateRange(sg: SubgroupResponse): string {
    if (!sg.startDate) return '—';
    const from = this.formatDate(sg.startDate);
    return sg.endDate ? `${from} - ${this.formatDate(sg.endDate)}` : from;
  }

  frequencyLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `fee.frequency_types.${value.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : value;
  }

  paymentFlowLabel(value: string | undefined): string {
    if (!value) return '—';
    const key = `fee.payment_flow_types.${value.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : value;
  }

  paymentStatusLabel(status: string | undefined): string {
    const key = (status || '').toLowerCase();
    if (!key) return '';
    const translationKey = `fee.status.${key}`;
    const translated = this.translate.instant(translationKey);
    return translated === translationKey ? (status || '') : translated;
  }

  get activeSubgroupFinalPrice(): number {
    const sg = this.activeSubgroup;
    if (!sg) return 0;
    const price = sg.price ?? 0;
    const dv    = sg.discountValue ?? 0;
    if (!dv || !sg.discountType) return price;
    return sg.discountType === 'PERCENT'
      ? price - (price * dv / 100)
      : price - dv;
  }

  openEditGroupName(): void {
    if (!this.group) return;
    const config = new MatDialogConfig<GroupNameEditDialogData>();
    config.data   = { groupId: this.group.id, currentName: this.group.name };
    config.width  = '420px';
    config.panelClass = 'fee-group-name-edit-dialog-panel';
    this.dialog.open(GroupNameEditDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((name: string | undefined) => {
        if (name && this.group) this.group = { ...this.group, name };
      });
  }

  openDeleteGroup(): void {
    if (!this.group) return;
    const hasData = this.group.subgroups.length > 0
      || this.group.subgroups.some(sg => (sg.memberCount ?? 0) > 0);

    const config = new MatDialogConfig<GroupDeleteDialogData>();
    config.data  = { groupId: this.group.id, groupName: this.group.name, hasData };
    config.width = '420px';
    this.dialog.open(GroupDeleteDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((deleted: boolean | undefined) => {
        if (deleted) this.router.navigate(['/fees', this.industryId]);
      });
  }

  openAddSubgroup(): void {
    if (!this.group) return;
    this.router.navigate(['/fees', this.industryId, 'groups', 'create'], {
      queryParams: {
        mode: 'edit',
        groupId: this.group.id,
        returnUrl: this.router.url
      }
    });
  }

  openEditSubgroup(): void {
    const sg = this.activeSubgroup;
    if (!sg) return;
    const config = new MatDialogConfig<SubgroupFormDialogData>();
    config.data  = { mode: 'edit', subgroup: sg };
    config.width = '860px';
    config.maxWidth = '96vw';
    config.panelClass = 'fee-subgroup-dialog-panel';
    this.dialog.open(SubgroupFormDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: SubgroupResponse | undefined) => {
        if (result && this.group) {
          const sgs = [...this.group.subgroups];
          sgs[this.activeSubgroupIndex] = result;
          this.group = { ...this.group, subgroups: sgs };
          this.refreshMemberDiscounts();
        }
      });
  }

  private refreshMemberDiscounts(): void {
    this.loadMembersForCurrentSelection();
  }

  openAddMember(fromSubgroupContext: boolean): void {
    this.router.navigate(['/fees', this.industryId, 'add-member'], {
      queryParams: {
        groupId: fromSubgroupContext ? this.groupId : undefined,
        subgroupId: fromSubgroupContext && this.activeSubgroup ? this.activeSubgroup.id : undefined,
        returnUrl: this.router.url
      }
    });
  }

  openDeleteSubgroup(): void {
    const sg = this.activeSubgroup;
    if (!sg) return;

    const config = new MatDialogConfig<SubgroupDeleteDialogData>();
    config.data  = {
      subgroupId: sg.id,
      subgroupName: sg.name,
      hasData: (sg.memberCount ?? 0) > 0
    };
    config.width = '420px';
    this.dialog.open(SubgroupDeleteDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((deleted: boolean | undefined) => {
        if (!deleted || !this.group) return;

        const idx = this.activeSubgroupIndex;
        const sgs = this.group.subgroups.filter((_, i) => i !== idx);

        if (sgs.length === 0) {
          this.router.navigate(['/fees', this.industryId]);
          return;
        }

        this.group = {...this.group, subgroups: sgs};
        this.activeSubgroupIndex = -1;
        if (this.tabScrollOffset > 0 && this.tabScrollOffset >= sgs.length) {
          this.tabScrollOffset = Math.max(0, sgs.length - 1);
        }
        this.loadMembersForCurrentSelection();
      });
  }

  private loadMembersForCurrentSelection(): void {
    if (!this.group) {
      this.memberRows = [];
      return;
    }

    this.memberProfileCache.clear();
    this.openedMemberMenuKey = null;
    this.isMembersLoading = true;

    if (this.activeSubgroup) {
      const subgroup = this.activeSubgroup;
      this.feeService.getMembersBySubgroup(subgroup.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(members => {
          this.memberRows = members.map(member => this.toMemberRow(member, subgroup));
          this.resetMembersVisibleCount();
          this.isMembersLoading = false;
        });
      return;
    }

    const subgroupDescriptors = this.group.subgroups.map(sg => ({ id: sg.id, subgroup: sg }));
    if (subgroupDescriptors.length === 0) {
      this.memberRows = [];
      this.isMembersLoading = false;
      return;
    }

    const requests = subgroupDescriptors.map(item =>
      this.feeService.getMembersBySubgroup(item.id)
    );

    forkJoin(requests.length > 0 ? requests : [of([] as FeeMemberResponse[])])
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.memberRows = response.flatMap((members, index) =>
          members.map(member => this.toMemberRow(member, subgroupDescriptors[index].subgroup))
        );
        this.resetMembersVisibleCount();
        this.isMembersLoading = false;
      });
  }

  openMemberProfile(memberId: number, subgroupId?: number): void {
    this.router.navigate(['/fees', 'user-profile', memberId], {
      queryParams: {industryId: this.industryId, subgroupId}
    });
  }

  toggleMemberMenu(member: GroupPageMemberRow, event: Event): void {
    if (!member.active) return;
    event.stopPropagation();
    const key = this.memberRowKey(member);
    if (this.openedMemberMenuKey === key) {
      this.closeMemberMenu();
      return;
    }
    this.openedMemberMenuKey = key;
    this.positionMemberMenu(event.currentTarget as HTMLElement);
    this.ensureMemberProfileLoaded(member.id);
  }

  closeMemberMenu(): void {
    this.openedMemberMenuKey = null;
    this.memberMenuPosition = null;
  }

  private positionMemberMenu(anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 96;
    const gap = 8;
    let top = rect.bottom + gap;
    let left = rect.right - menuWidth;

    if (top + menuHeight > window.innerHeight - gap) {
      top = rect.top - menuHeight - gap;
    }

    left = Math.max(gap, Math.min(left, window.innerWidth - menuWidth - gap));
    top = Math.max(gap, top);

    this.memberMenuPosition = { top, left };
  }

  openEditMemberInfo(member: GroupPageMemberRow): void {
    if (!member.active) return;
    this.closeMemberMenu();
    this.feeService.getMemberProfile(member.id)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(profile => {
        this.memberProfileCache.set(member.id, profile);
        const config = new MatDialogConfig<EditPersonalInfoDialogData>();
        const subgroup = profile.subgroups.find(s => s.id === member.subgroupId);
        config.data = { memberId: member.id, profile, subgroup };
        config.panelClass = 'fee-dialog-panel';
        this.dialog.open(FeeEditPersonalInfoDialogComponent, config)
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((updated: MemberProfileResponse | undefined) => {
            if (updated) {
              this.memberProfileCache.set(member.id, updated);
              this.updateMemberRowsFromProfile(member, updated);
            }
          });
      });
  }

  sendMemberNotification(member: GroupPageMemberRow): void {
    if (!member.active) {
      return;
    }
    const rowKey = this.memberRowKey(member);
    if (this.sendingNotificationMemberKey === rowKey) {
      return;
    }
    this.closeMemberMenu();
    this.sendingNotificationMemberKey = rowKey;
    this.feeService.sendMemberNotification(member.id, member.subgroupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendingNotificationMemberKey = null;
          this.showSnackbar(
            MockupDataModel.success(this.translate.instant('fee.messages.payment_reminder_sent')),
            SnackBarUtil.CLASSES.success
          );
        },
        error: (err: HttpErrorResponse) => {
          this.sendingNotificationMemberKey = null;
          if (err?.status === 429) {
            this.showSnackbar(
              MockupDataModel.error(this.translate.instant('fee.messages.notification_cooldown')),
              SnackBarUtil.CLASSES.error
            );
            return;
          }
          this.showSnackbar(
            MockupDataModel.error(this.translate.instant('fee.messages.failed_send_notification')),
            SnackBarUtil.CLASSES.error
          );
        }
      });
  }

  toggleMemberActive(member: GroupPageMemberRow, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const nextActive = checked;
    const prevActive = member.active ?? true;
    member.active = nextActive;
    if (!nextActive) {
      this.closeMemberMenu();
    }
    this.feeService.setEnrollmentActive(member.id, member.subgroupId, nextActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          member.active = prevActive;
          this.memberRows
            .filter(row => row.id === member.id && row.subgroupId === member.subgroupId)
            .forEach(row => { row.active = prevActive; });
        }
      });
    this.memberRows
      .filter(row => row.id === member.id && row.subgroupId === member.subgroupId)
      .forEach(row => { row.active = nextActive; });
  }

  private ensureMemberProfileLoaded(memberId: number): void {
    if (this.memberProfileCache.has(memberId)) {
      return;
    }
    this.feeService.getMemberProfile(memberId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(profile => this.memberProfileCache.set(memberId, profile));
  }

  private updateMemberRowsFromProfile(sourceRow: GroupPageMemberRow, profile: MemberProfileResponse): void {
    const rows = this.memberRows.filter(row => row.id === sourceRow.id);
    for (const row of rows) {
      row.name = profile.name;
      row.frequency = this.frequencyLabel(profile.frequency);
      row.active = profile.active !== false;
      const subgroup = profile.subgroups.find(s => s.id === row.subgroupId);
      if (subgroup) {
        row.discount = this.formatDiscount(subgroup.discountValue, subgroup.discountType);
      }
      const payments = profile.paymentHistory.filter(p => p.subgroupId === row.subgroupId);
      const current = payments.find(p => p.isCurrentPeriod) ?? payments[payments.length - 1];
      if (current) {
        row.paymentStatus = (current.status || '').toUpperCase();
        row.due = current.due ?? 0;
        row.total = current.total ?? 0;
      }
    }
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data,
    });
  }

  private formatDiscount(value: number | undefined, type: string | undefined): string {
    return value
      ? (type === 'PERCENT' ? `${value}%` : `${value} ${this.currencySymbol}`)
      : '—';
  }

  private formatMemberDiscount(member: FeeMemberResponse, subgroup: SubgroupResponse): string {
    return this.formatDiscount(
      member.discountValue ?? subgroup.discountValue,
      member.discountType ?? subgroup.discountType
    );
  }

  private toMemberRow(member: FeeMemberResponse, subgroup: SubgroupResponse): GroupPageMemberRow {
    const discount = this.formatMemberDiscount(member, subgroup);
    return {
      id: member.id,
      subgroupId: member.subgroupId,
      name: member.name,
      frequency: this.frequencyLabel(member.frequency),
      discount,
      due: 0,
      total: member.currentPeriodPaid ?? 0,
      active: member.active ?? true,
      paymentStatus: member.currentPeriodStatus ?? 'PENDING'
    };
  }

  private resetMembersVisibleCount(): void {
    this.membersVisibleCount = this.loadMorePageSize;
  }
}
